import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * Runtime window CLASSES — the layout/URL contract's answer to a pageKey nobody registered
 * yet (gh#2517, G7-D PR-1).
 *
 * ── THE GAP THIS CLOSES ───────────────────────────────────────────────────────
 * `pageComponentRegistry` is a module-level map. Its STATIC keys exist from the first
 * import, so a snapshot naming `ChatPage` resolves on any load. A RUNTIME key does not:
 * `openUserViewWindow` registers `UserViewWindow-<id>` when the operator clicks, and the
 * next page load starts from the baseline map with that key gone. The layout snapshot
 * records a stable, meaningful key and then cannot restore it — silently, because the
 * registry probe is fail-soft by design.
 *
 * A class is what makes such a key ADDRESSABLE without a live registration: a prefix that
 * says which family a key belongs to, a parse that says whether the key is a well-formed
 * member of it, and the two derivations a window needs — how to BUILD its component and
 * how to DESCRIBE its chrome. With that, the snapshot and the URL can both carry
 * `UserViewWindow-5` and both can rebuild it after a reload.
 *
 * ── EVERYTHING IS DERIVED FROM THE KEY, AND FROM ONE PARSE ────────────────────
 * A class NEVER reads the payload it is asked about: the label, the route, the slug and
 * the size all come from the pageKey, which is the one field the store already treats as
 * identity. A payload-carried title is precisely what `applyLayout` refuses for allowlist
 * pages, and a runtime class must not be the weaker door.
 *
 * {@link RuntimeWindowClass.describe} and {@link RuntimeWindowClass.resolve} are two views
 * of ONE parse per class, and they must agree: `describe` is what the capture side and the
 * URL writer consult, `resolve` is what the apply side rehydrates from. A class whose two
 * halves disagreed would let capture record a window that apply then drops — the exact
 * property the layout verbs are built not to have. Pinned per class by test.
 *
 * ── PERMISSION IS THE LAUNCHER'S, MIRRORED ────────────────────────────────────
 * {@link RuntimeWindowClass.permitted} answers "may THIS viewer open windows of this
 * class", and it answers it from the viewer's OWN windowable-page allowlist — the list
 * `useWindowablePages()` produces after `filterByRole` / `filterByPermissions` /
 * `canAccessRole`. So a class is permitted exactly when the launcher path that mints its
 * keys is permitted, and it inherits any future tightening of that path for free.
 *
 * That is deliberately the ONLY axis. Handing classes a second viewer signal (a raw role,
 * a permission array) would put two filters in front of one door, and eligibility split
 * across two axes is how a window gets captured under one and dropped under the other.
 */

/**
 * Everything a window of a runtime class needs, derived from its pageKey alone.
 *
 * Structurally this is the runtime-key counterpart of what `useWindowablePages()` supplies
 * for a static page (`pluginSlug` / `pageRoute` / `label` / `windowable.defaultSize`), so
 * the URL contract can resolve both kinds of key through one shape.
 */
export interface RuntimeWindowDescriptor {
  /** Owning surface slug — the compositor renders it in the window's error/skeleton chrome. */
  pluginSlug: string;
  /**
   * The full-page route this window mirrors — CHROME ONLY, and the honest reading of that is
   * that nothing navigates here today.
   *
   * A static page's `pageRoute` is where its "open as page" control goes. A runtime class's is
   * a field with the same name and no such control behind it: no compositor surface reads
   * `WindowState.pageRoute` to navigate, so what a class writes here is a string the window
   * carries and nothing follows. The affordance is not built.
   *
   * Two consequences worth having in one place rather than rediscovered per class:
   *   · a route spelled here is NOT checked against `App.tsx`. The
   *     `core-page-routes-parity` guard reads `CORE_WINDOWABLE_PAGES` and nothing else, so a
   *     runtime class sits outside it — `/user-view/<id>` is currently a route the router does
   *     not register at all, and the guard is silent because it was never asked;
   *   · the value is still DERIVED from the key rather than carried by a payload, and that
   *     stays true whether or not anything ever follows it. The day a window grows the
   *     control, the string it goes to must already be one the key decided.
   */
  pageRoute: string;
  /** Canonical window title. Derived from the key; never a string off the wire. */
  label: string;
  /** Frame size for a window this class opens with no geometry of its own. */
  defaultSize: { width: number; height: number };
}

/**
 * What a class is allowed to know about the viewer: the pageKeys they may open.
 *
 * Structurally satisfied by indexing `useWindowablePages()` output — the list that already
 * carries the role + permission filter. See the header for why the context stops here.
 */
export interface RuntimeWindowViewer {
  allowedPageKeys: ReadonlySet<string>;
}

/** One family of runtime-registered pageKeys, addressable without a live registration. */
export interface RuntimeWindowClass {
  /** Stable identifier for logs and tests. Not part of any wire format. */
  id: string;
  /**
   * The literal pageKey prefix that names this family. Membership is a prefix match with a
   * NON-EMPTY remainder — the bare prefix is not a key, it is the family's own name.
   */
  prefix: string;
  /** May this viewer open windows of this class? Mirrors the launcher path that mints them. */
  permitted(viewer: RuntimeWindowViewer): boolean;
  /** Chrome for `pageKey`, or `undefined` when it is not a well-formed member of this class. */
  describe(pageKey: string): RuntimeWindowDescriptor | undefined;
  /** The component for `pageKey`, or `undefined` for a key this class cannot address. */
  resolve(pageKey: string): LazyExoticComponent<ComponentType<any>> | undefined;
}

// ─── UserViewWindow — saved personal views (gh#1199, gh#2517) ─────────────────

/** The pageKey family `openUserViewWindow` mints, one stable key per saved view. */
export const USER_VIEW_WINDOW_PREFIX = 'UserViewWindow-';

/**
 * The launcher entry whose reachability this class mirrors.
 *
 * The ONE affordance that opens a personal-view window is PM's "Masaüstünde aç" button, so
 * a viewer who may open PM may open the windows PM hands out, and a viewer who may not,
 * may not. Per-view authorization is NOT this gate's job and must not be faked here: the
 * spec fetch (`GET /entity/user-views/:id`) is self-scoped and answers 404 for another
 * user's view, so a link naming a foreign id restores a window that renders its own
 * not-found — the server stays the one door that decides, exactly as the dm surfaces argue
 * for their own `requiredRole: undefined`.
 *
 * Pinned against `CORE_WINDOWABLE_PAGES` by test: a key that stopped existing there would
 * silently turn this class off for everybody rather than fail loudly.
 */
export const PM_PAGE_KEY = 'PmPage';

/**
 * The frame a personal-view window opens at — the SOURCE of that size, not a copy of one.
 *
 * `openUserViewWindow` reads it from this class's descriptor rather than declaring its own
 * (it used to declare a local `DEFAULT_SIZE`, which is the sibling copy gh#2517 removed), so
 * a view window is the same size whichever path opened it: a click, a restored snapshot, or
 * a `?win=` link.
 *
 * PM's launcher entry (`CORE_WINDOWABLE_PAGES`) declares the same numbers independently, and
 * deliberately — the launcher's PM window and a personal-view window are meant to match. Two
 * declarations means the intent is a claim rather than a consequence, so it is pinned by test.
 */
const USER_VIEW_WINDOW_SIZE = { width: 720, height: 520 } as const;

/**
 * The view id in `pageKey`, or `undefined`.
 *
 * ONE SPELLING PER VIEW, which is why this is a regex rather than `Number()`. `pageKey` IS
 * the identity the snapshot, the URL and the store's `seen` set all dedupe on, so if
 * `UserViewWindow-05` and `UserViewWindow-5` both parsed, one view could occupy two entries
 * and open two windows. No sign, no leading zero, no whitespace, no exponent — the same
 * discipline `desktopUrl`'s geometry regex applies for the same reason.
 *
 * Ten digits stays inside the safe-integer range, and a bounded length keeps a hand-edited
 * link from asking this app to address an id no row can have.
 */
const USER_VIEW_ID_RE = /^[1-9][0-9]{0,9}$/;

function parseUserViewId(pageKey: string): number | undefined {
  if (typeof pageKey !== 'string' || !pageKey.startsWith(USER_VIEW_WINDOW_PREFIX)) return undefined;
  const suffix = pageKey.slice(USER_VIEW_WINDOW_PREFIX.length);
  if (!USER_VIEW_ID_RE.test(suffix)) return undefined;
  const id = Number(suffix);
  return Number.isSafeInteger(id) ? id : undefined;
}

/**
 * The pageKey for a saved view — the ONE place the format is written.
 *
 * Total on purpose: an id this class cannot address still produces a key, and that key is
 * then REFUSED by `describe`/`resolve`. The alternative (throwing here) would move a
 * fail-closed refusal into the caller's click handler.
 */
export function userViewPageKey(viewId: number): string {
  return `${USER_VIEW_WINDOW_PREFIX}${viewId}`;
}

/**
 * Saved personal views, opened as floating desktop windows.
 *
 * Declared at MODULE SCOPE, which is the whole point: the class has to exist on a cold load
 * — before any window has been opened — or the snapshot it is meant to restore still cannot
 * be restored. Only the per-view COMPONENT is built on demand.
 */
export const USER_VIEW_WINDOW_CLASS: RuntimeWindowClass = {
  id: 'user-view-window',
  prefix: USER_VIEW_WINDOW_PREFIX,

  permitted(viewer) {
    return viewer.allowedPageKeys.has(PM_PAGE_KEY);
  },

  describe(pageKey) {
    const viewId = parseUserViewId(pageKey);
    if (viewId === undefined) return undefined;
    return {
      pluginSlug: `user-view-${viewId}`,
      pageRoute: `/user-view/${viewId}`,
      // The view's real NAME lives on the server and this label may not wait for a fetch,
      // so the id is what distinguishes two restored view windows from each other. A window
      // re-used by apply keeps its live title, so this is what a COLD restore shows.
      label: `Kişisel Görünüm #${viewId}`,
      defaultSize: { ...USER_VIEW_WINDOW_SIZE },
    };
  },

  resolve(pageKey) {
    const viewId = parseUserViewId(pageKey);
    if (viewId === undefined) return undefined;
    // The closure binds `viewId` because PluginWindowAdapter renders registry components
    // props-less — the same reason `openUserViewWindow` has always registered per view.
    return lazy(async () => {
      const { UserViewWindow } = await import('../modules/pm/UserViewWindow.js');
      return {
        default: function UserViewWindowBound() {
          return UserViewWindow({ viewId });
        },
      };
    });
  },
};

// ─── DmItemWindow — one dm item, opened as its own window (SRS G7-D PR-2) ─────

/** The pageKey family `openDmItemWindow` mints, one stable key per item. */
export const DM_ITEM_WINDOW_PREFIX = 'DmItemWindow-';

/**
 * The launcher entry whose reachability this class mirrors.
 *
 * The affordances that open an item window all live on `/dm/board` (its rows, its panes,
 * its inspector), so a viewer who may open that page may open the windows it hands out. Per
 * ITEM authorization is NOT this gate's job and must not be faked here, by the argument
 * {@link PM_PAGE_KEY} makes in full: the reads behind this window (`GET /dm/items/:id` and
 * its siblings) are gated on `pm.item.read` / `pm.edge.read` server-side, so a link naming an
 * item the viewer may not read restores a window that renders its own in-panel 403.
 *
 * Pinned against `CORE_WINDOWABLE_PAGES` by test, for the same reason: a key that stopped
 * existing there would silently turn this class off for everybody rather than fail loudly.
 */
export const DM_PROJECT_PAGE_KEY = 'DmProjectView';

/**
 * The frame an item window opens at — the dm SINGLE-COLUMN default, and a deliberate
 * departure from the frame of the page this window's route points at.
 *
 * The board's (`/dm/board`) launcher entry declares 980×620, and says why in its own comment: it
 * carries a pane region AND a fixed-width inspector column beside it. Neither is here. This
 * window is one `ItemDetail` in one column — the shape `/dm/memory` describes when it keeps
 * the shared 760×560 rather than departing from it — so the wider frame's argument does not
 * transfer and copying the number would import a reason that is not true of this surface.
 *
 * ONE declaration, so there is nothing for it to drift from. That is the difference from
 * {@link USER_VIEW_WINDOW_SIZE}, which is pinned by test because two files declare it and
 * mean it to match; asserting equality with a dm sibling here would pin a coincidence
 * instead of an intent, and a counter-pin over a coincidence goes stale by design.
 */
const DM_ITEM_WINDOW_SIZE = { width: 760, height: 560 } as const;

/**
 * The `<projectId>-<itemNo>` pair in `pageKey`, or `undefined`.
 *
 * EXACTLY TWO SEGMENTS, both positive integers in ONE spelling each, for the reason
 * {@link USER_VIEW_ID_RE} states and one that is new here. The old reason first: `pageKey` IS
 * the identity the snapshot, the URL and the store's `seen` set dedupe on, so admitting
 * `DmItemWindow-1-07` beside `DmItemWindow-1-7` would let one item occupy two entries and
 * open two windows.
 *
 * The new one is the SEPARATOR. A key is a flat string and `-` is both the prefix's last
 * character and the field delimiter, so a third segment has to be refused rather than
 * ignored: `DmItemWindow-1-2-3` read leniently is project 1 item 2 with a suffix nobody
 * parsed, and the window it opened would not be the window the key named. The digits-only
 * alphabet is what makes the split unambiguous — no segment can contain the delimiter.
 *
 * Ten digits per segment stays inside the safe-integer range, and keeps a hand-edited link
 * from asking this app to address a row no table can have.
 */
const DM_ITEM_KEY_RE = /^([1-9][0-9]{0,9})-([1-9][0-9]{0,9})$/;

/**
 * The `(projectId, itemNo)` a dm item window's `pageKey` names, or `undefined`.
 *
 * EXPORTED as of G7-D PR-5, and the export is what keeps a second reader honest. The
 * workspace hub asks "which of the desktop's open windows belong to THIS project", and the
 * obvious spelling of that question — `pageKey.startsWith('DmItemWindow-' + projectId + '-')`
 * — is a second copy of the key format, written by a caller that cannot see
 * {@link DM_ITEM_KEY_RE}. Two copies of a wire format is exactly the drift
 * {@link openDmItemWindow}'s header refuses for the mint side, and a reader's copy fails in a
 * way the minter's cannot: a lenient prefix match ADMITS keys this class would refuse
 * (`DmItemWindow-1-2-3`) and would list a window the layout contract cannot address.
 *
 * So the one parser answers both, and a caller that gets `undefined` has been told the key is
 * not this class's — the same fail-closed answer `describe` gives.
 */
export function parseDmItemPageKey(
  pageKey: string,
): { projectId: number; itemNo: number } | undefined {
  if (typeof pageKey !== 'string' || !pageKey.startsWith(DM_ITEM_WINDOW_PREFIX)) return undefined;
  const matched = DM_ITEM_KEY_RE.exec(pageKey.slice(DM_ITEM_WINDOW_PREFIX.length));
  if (!matched) return undefined;
  const projectId = Number(matched[1]);
  const itemNo = Number(matched[2]);
  if (!Number.isSafeInteger(projectId) || !Number.isSafeInteger(itemNo)) return undefined;
  return { projectId, itemNo };
}

/**
 * The pageKey for one dm item — the ONE place the format is written.
 *
 * Total on purpose, exactly like {@link userViewPageKey}: a pair this class cannot address
 * still produces a key, and that key is then REFUSED by `describe`/`resolve`, so the
 * fail-closed decision stays in one place instead of moving into every click handler.
 */
export function dmItemPageKey(projectId: number, itemNo: number): string {
  return `${DM_ITEM_WINDOW_PREFIX}${projectId}-${itemNo}`;
}

/**
 * A dm item, opened as a floating desktop window.
 *
 * Declared at MODULE SCOPE for the reason {@link USER_VIEW_WINDOW_CLASS} gives: the class has
 * to exist on a cold load, before any window has been opened, or the snapshot naming its key
 * still cannot be restored. Only the per-item COMPONENT is built on demand.
 */
export const DM_ITEM_WINDOW_CLASS: RuntimeWindowClass = {
  id: 'dm-item-window',
  prefix: DM_ITEM_WINDOW_PREFIX,

  permitted(viewer) {
    return viewer.allowedPageKeys.has(DM_PROJECT_PAGE_KEY);
  },

  describe(pageKey) {
    const parsed = parseDmItemPageKey(pageKey);
    if (parsed === undefined) return undefined;
    return {
      pluginSlug: `dm-item-${parsed.projectId}-${parsed.itemNo}`,
      // CHROME, and NOTHING FOLLOWS IT TODAY — see {@link RuntimeWindowDescriptor.pageRoute}:
      // no surface navigates a window to its own route, so this is a string the window
      // carries rather than a destination anything reaches. It is written correctly anyway,
      // for the reason that field states, and the rest of this comment is about what
      // "correctly" means here.
      //
      // NO `?project=` SEGMENT, and the omission is load-bearing rather than a shortcut.
      // `DmProjectView` canonicalizes a slug-less address by filling in the project it
      // resolved for this tenant, so a route naming none lands on the workspace's own
      // platform project — while a route naming one this tenant does not have would trip
      // that page's `slugMismatch` arm and refuse. The window carries a projectId the route
      // cannot express, and today those two are the same project because there is exactly
      // one; REVISIT THIS LINE, the label below and the key format together if dm ever
      // addresses more than one project, or if gh#2550's id-shaped route lands.
      //
      // `/dm/board` since gh#2607 PR-4, with the launcher's five dm entries — the pre-shell
      // `/dm-project` still forwards here, so this is not a repair but a stop removed. The
      // two moved TOGETHER because they are one claim about where the board lives.
      //
      // NO `?mode=` SEGMENT EITHER, and it is now a DECISION rather than an omission
      // nobody had to make. The route inherits `dmProjectViewUrl`'s default mode, which was
      // `stack` and is `tree` since the expansion wave — so "open as page" on an item
      // window lands on the TREE with that item inspected instead of on the Pane Stack.
      // Deliberate: operator decision 2026-09-04. The window is about ONE item, and the
      // surface that shows an item in its context is the tree; the Pane Stack is a reading
      // of a selection, which a single-item route has not made.
      //
      // Writing `mode=` here to pin today's surface is the thing NOT to do — it would pin
      // this route past the release where the default moves again, which is the failure the
      // URL module's omission rule exists to prevent.
      pageRoute: `/dm/board?inspect=${parsed.itemNo}`,
      // The item's real TITLE lives on the server and this label may not wait for a fetch,
      // so the number an operator says aloud is what distinguishes two restored item
      // windows. A window re-used by apply keeps its live title; this is the COLD restore.
      // English, by the dm-surface rule its launcher entries state.
      label: `Item #${parsed.itemNo}`,
      defaultSize: { ...DM_ITEM_WINDOW_SIZE },
    };
  },

  resolve(pageKey) {
    const parsed = parseDmItemPageKey(pageKey);
    if (parsed === undefined) return undefined;
    // The closure binds the PAIR because PluginWindowAdapter renders registry components
    // props-less — the same reason the sibling class binds its view id. The content is
    // pinned to this item; the route above is only the "open as page" chrome.
    return lazy(async () => {
      const { DmItemWindow } = await import('../modules/pm/DmItemWindow.js');
      return {
        default: function DmItemWindowBound() {
          return DmItemWindow({ projectId: parsed.projectId, itemNo: parsed.itemNo });
        },
      };
    });
  },
};

/**
 * Every runtime window class the app ships.
 *
 * Module scope, for the reason {@link USER_VIEW_WINDOW_CLASS} gives: a roster assembled at
 * render time would be empty on the load that has to restore a layout.
 */
export const RUNTIME_WINDOW_CLASSES: readonly RuntimeWindowClass[] = [
  USER_VIEW_WINDOW_CLASS,
  DM_ITEM_WINDOW_CLASS,
];

/**
 * The classes THIS viewer may open — the single derivation both layout surfaces consume.
 *
 * The filter is applied ONCE, here, so what reaches `captureLayout`/`applyLayout` already
 * means "classes this viewer may open", exactly as `allowedPages` already means "pages this
 * viewer may open". The store has no viewer and must not acquire one.
 */
export function permittedRuntimeWindowClasses(
  viewer: RuntimeWindowViewer,
  classes: readonly RuntimeWindowClass[] = RUNTIME_WINDOW_CLASSES,
): readonly RuntimeWindowClass[] {
  return classes.filter((cls) => cls.permitted(viewer) === true);
}

/**
 * The class that claims `pageKey`, or `undefined`. Prefix match with a non-empty remainder;
 * a missing / non-array roster resolves to nothing, which is the fail-closed direction.
 *
 * A MATCH IS NOT ADMISSION — it only says which class should be asked. Whether the key is a
 * well-formed member is {@link RuntimeWindowClass.describe}'s answer, and callers should
 * reach for {@link describeRuntimeWindow} rather than treating a prefix hit as eligibility.
 */
export function matchRuntimeWindowClass(
  pageKey: string,
  classes: readonly RuntimeWindowClass[] | undefined,
): RuntimeWindowClass | undefined {
  if (!Array.isArray(classes)) return undefined;
  if (typeof pageKey !== 'string' || pageKey.length === 0) return undefined;
  for (const cls of classes) {
    if (!cls || typeof cls.prefix !== 'string' || cls.prefix.length === 0) continue;
    if (pageKey.length <= cls.prefix.length) continue;
    if (pageKey.startsWith(cls.prefix)) return cls;
  }
  return undefined;
}

/**
 * Resolve `pageKey` against a viewer's permitted classes: the claiming class AND the chrome
 * it derives, or `undefined` when no class admits the key.
 *
 * The ONE eligibility question every caller asks — capture (may this window be recorded),
 * apply (may this entry be opened), and the URL contract (what does this window look like).
 * Asking it in one place is what keeps the three from disagreeing about the same key.
 */
export function describeRuntimeWindow(
  pageKey: string,
  classes: readonly RuntimeWindowClass[] | undefined,
): { cls: RuntimeWindowClass; descriptor: RuntimeWindowDescriptor } | undefined {
  const cls = matchRuntimeWindowClass(pageKey, classes);
  if (!cls) return undefined;
  const descriptor = cls.describe(pageKey);
  return descriptor ? { cls, descriptor } : undefined;
}
